import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';
import ImageCropModal from '../../components/ImageCropModal';

interface JoinStep {
  id: string;
  title: string;
  subTitle: string;
  description: string;
}

const EMPTY = { title: '', subTitle: '', description: '', language: 'ru' };

export default function AdminJoinPage() {
  const [steps, setSteps] = useState<JoinStep[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [picture, setPicture] = useState<File | null>(null);
  const [pendingPicture, setPendingPicture] = useState<File | null>(null);

  const load = () =>
    axios.get(`${API_BASE}/join-section/get-all`).then(r => setSteps(r.data));

  useEffect(() => { load(); }, []);

  const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (f) setPendingPicture(f);
    e.target.value = '';
  };

  const startEdit = (s: JoinStep) => {
    setEditId(s.id);
    setForm({ title: s.title, subTitle: s.subTitle || '', description: s.description, language: (s as any).language || 'ru' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancel = () => { setEditId(null); setForm(EMPTY); setPicture(null); setPendingPicture(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/join-section/update/${editId}`, form);
      } else {
        const fd = new FormData();
        fd.append('joinSectionDtoRequest', new Blob([JSON.stringify(form)], { type: 'application/json' }));
        if (picture) fd.append('picture', picture);
        await api.post('/join-section/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      cancel();
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Удалить шаг?')) return;
    await api.delete(`/join-section/delete-by-id/${id}`);
    await load();
  };

  return (
    <div style={s.page}>
      <h2 style={s.pageTitle}>Управление разделом «Как вступить»</h2>

      {/* Форма */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>{editId ? '✏️ Редактировать шаг' : '➕ Добавить шаг'}</h3>
        <form onSubmit={handleSubmit} style={s.form}>
          <input
            style={s.input}
            placeholder="Заголовок шага *"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            required
          />
          <input
            style={s.input}
            placeholder="Подзаголовок (необязательно)"
            value={form.subTitle}
            onChange={e => setForm({ ...form, subTitle: e.target.value })}
          />
          <textarea
            style={{ ...s.input, resize: 'vertical' }}
            placeholder="Описание шага *"
            rows={4}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            required
          />
          <select style={s.input} value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
            <option value="ru">🇷🇺 Русский</option>
            <option value="ky">🇰🇬 Кыргызский</option>
            <option value="en">🇬🇧 English</option>
          </select>
          {!editId && (
            <div>
              <label style={s.fileLabel}>
                🖼️ Изображение для шага
                <input
                  type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={handlePictureChange}
                />
              </label>
              {picture && <span style={s.fileName}>{picture.name}</span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnPrimary} type="submit" disabled={saving}>
              {saving ? 'Сохранение...' : editId ? '💾 Сохранить' : '➕ Добавить'}
            </button>
            {editId && <button type="button" onClick={cancel} style={s.btnCancel}>Отмена</button>}
          </div>
        </form>
      </div>

      {/* Список шагов */}
      <div style={s.card}>
        <h3 style={s.cardTitle}>Все шаги ({steps.length})</h3>
        {steps.length === 0 ? (
          <p style={s.empty}>Шагов пока нет</p>
        ) : (
          steps.map((step, i) => (
            <div key={step.id} style={s.row}>
              <div style={s.stepNum}>{i + 1}</div>
              <div style={s.rowInfo}>
                <div style={s.rowTitle}>{step.title}</div>
                {step.subTitle && <div style={s.rowSub}>{step.subTitle}</div>}
                <div style={s.rowDesc}>
                  {step.description.slice(0, 120)}{step.description.length > 120 ? '...' : ''}
                </div>
              </div>
              <button onClick={() => startEdit(step)} style={s.btnEdit}>✏️</button>
              <button onClick={() => remove(step.id)} style={s.btnDanger}>🗑</button>
            </div>
          ))
        )}
      </div>

      <ImageCropModal
        file={pendingPicture}
        aspect={4 / 3}
        title="Обрезать изображение для шага"
        onCancel={() => setPendingPicture(null)}
        onCropped={cropped => { setPicture(cropped); setPendingPicture(null); }}
      />
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { padding: 32, maxWidth: 900, margin: '0 auto' },
  pageTitle: { color: '#1a3a6b', fontSize: '1.6rem', marginBottom: 24 },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 16, overflow: 'hidden' },
  cardTitle: { color: '#1a3a6b', fontSize: '1rem', padding: '16px 20px', borderBottom: '1px solid #f0f4fb' },
  form: { display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 20px 20px' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.95rem', outline: 'none' },
  fileLabel: { display: 'inline-block', padding: '8px 16px', background: '#f0f4fb', border: '1px dashed #1a3a6b', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem', color: '#1a3a6b', fontWeight: 600 },
  fileName: { marginLeft: 10, fontSize: '0.85rem', color: '#555' },
  btnPrimary: { padding: '10px 24px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnCancel: { padding: '10px 20px', background: '#f0f4fb', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer' },
  empty: { padding: '16px 20px', color: '#888', fontSize: '0.9rem' },
  row: { display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 20px', borderTop: '1px solid #f0f4fb' },
  stepNum: { minWidth: 32, height: 32, borderRadius: '50%', background: '#1a3a6b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 },
  rowInfo: { flex: 1 },
  rowTitle: { fontWeight: 700, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 2 },
  rowSub: { fontSize: '0.85rem', color: '#1a3a6b', fontWeight: 600, marginBottom: 4 },
  rowDesc: { fontSize: '0.85rem', color: '#666' },
  btnEdit: { padding: '6px 10px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
  btnDanger: { padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 },
};
